import {
	Button,
	Group,
	Modal,
	NumberInput,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

interface DialogProps {
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	isPrompt?: boolean;
	defaultValue?: string;
	onConfirm: (value?: string) => void;
	onCancel?: () => void;
}

interface ConfirmationDialogContextType {
	confirm: (
		title: string,
		message: string,
		confirmText?: string,
		cancelText?: string,
	) => Promise<boolean>;
	prompt: (
		title: string,
		message: string,
		defaultValue?: string,
		confirmText?: string,
		cancelText?: string,
	) => Promise<string | null>;
}

const ConfirmationDialogContext =
	createContext<ConfirmationDialogContextType | null>(null);

export function ConfirmationDialogProvider({
	children,
}: { children: React.ReactNode }) {
	const [opened, { open, close }] = useDisclosure(false);
	const [dialogProps, setDialogProps] = useState<DialogProps | null>(null);
	const [promptValue, setPromptValue] = useState("");

	const confirm = useCallback(
		(
			title: string,
			message: string,
			confirmText = "Confirm",
			cancelText = "Cancel",
		) => {
			return new Promise<boolean>((resolve) => {
				setDialogProps({
					title,
					message,
					confirmText,
					cancelText,
					onConfirm: () => {
						close();
						resolve(true);
					},
					onCancel: () => {
						close();
						resolve(false);
					},
				});
				open();
			});
		},
		[open, close],
	);

	const prompt = useCallback(
		(
			title: string,
			message: string,
			defaultValue = "",
			confirmText = "Confirm",
			cancelText = "Cancel",
		) => {
			return new Promise<string | null>((resolve) => {
				setPromptValue(defaultValue);
				setDialogProps({
					title,
					message,
					confirmText,
					cancelText,
					isPrompt: true,
					defaultValue,
					onConfirm: (value) => {
						close();
						resolve(value || null);
					},
					onCancel: () => {
						close();
						resolve(null);
					},
				});
				open();
			});
		},
		[open, close],
	);

	const contextValue = useMemo(
		() => ({
			confirm,
			prompt,
		}),
		[confirm, prompt],
	);

	return (
		<ConfirmationDialogContext.Provider value={contextValue}>
			{children}
			<ConfirmationDialog
				opened={opened}
				dialogProps={dialogProps}
				promptValue={promptValue}
				setPromptValue={setPromptValue}
				onClose={close}
			/>
		</ConfirmationDialogContext.Provider>
	);
}

interface ConfirmationDialogComponentProps {
	opened: boolean;
	dialogProps: DialogProps | null;
	promptValue: string;
	setPromptValue: (value: string) => void;
	onClose: () => void;
}

const ConfirmationDialog = ({
	opened,
	dialogProps,
	promptValue,
	setPromptValue,
	onClose,
}: ConfirmationDialogComponentProps) => {
	if (!dialogProps) return null;

	const handleConfirm = useCallback(() => {
		dialogProps.onConfirm(dialogProps.isPrompt ? promptValue : undefined);
	}, [dialogProps, promptValue]);

	return (
		<Modal
			opened={opened}
			onClose={dialogProps.onCancel ?? onClose}
			title={
				<Title order={3} fw={800} size="h5">
					{dialogProps.title}
				</Title>
			}
			centered
			size="md"
			radius="md"
			padding={0}
			styles={{
				header: {
					paddingBottom: "1rem",
					minHeight: "auto",
				},
			}}
		>
			<Stack gap="md">
				<Text size="sm" c="dimmed" style={{ whiteSpace: "pre-line" }}>
					{dialogProps.message}
				</Text>

				{dialogProps.isPrompt && (
					<NumberInput
						id="confirmation-dialog-input"
						value={promptValue}
						onChange={(value) => setPromptValue(value.toString())}
						allowDecimal={false}
						placeholder="Amount"
						min={1}
						step={1}
						thousandSeparator=","
					/>
				)}

				<Group justify="flex-end" gap="xs" mt="lg">
					<Button
						variant="subtle"
						onClick={dialogProps.onCancel}
						size="xs"
						radius="md"
					>
						{dialogProps.cancelText}
					</Button>
					<Button onClick={handleConfirm} size="xs" radius="md">
						{dialogProps.confirmText}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
};

export function useConfirmationDialog() {
	const context = useContext(ConfirmationDialogContext);
	if (!context) {
		throw new Error(
			"useConfirmationDialog must be used within a ConfirmationDialogProvider",
		);
	}
	return context;
}
